/* ============================================================
   checkoutPhoneView — 527b0a7f (Artem 2026-08-30): pure view
   models for the checkout recipient phone. Mirrors the backend
   PhoneNumbers contract (optional leading +, digits/spaces/dashes/
   parentheses, 10–15 digits) and decides when the field is
   required: courier deliveries and carrier-ПВЗ pickups need a
   reachable recipient; pure provider self-pickup does not.
   Pure functions only — no React, no fetch. Node-testable.
   ============================================================ */

const PHONE_SHAPE = /^[+]?[0-9 ()-]+$/;

export type PhoneParse =
  | { ok: true; phone: string | null } // trimmed value (null = not provided)
  | { ok: false; error: string };

/** Prefill for the input: the profile number, or '' when absent. */
export function buildCheckoutPhoneDraft(profilePhone: string | null | undefined): string {
  return (profilePhone ?? '').trim();
}

/** Validate the typed value exactly like the server does. */
export function parseCheckoutPhone(raw: string): PhoneParse {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, phone: null };
  if (!PHONE_SHAPE.test(trimmed)) {
    return { ok: false, error: 'Телефон — только цифры, пробелы, дефисы и скобки.' };
  }
  const digits = trimmed.replace(/\D/g, '').length;
  if (digits < 10 || digits > 15) {
    return { ok: false, error: 'Телефон должен содержать 10–15 цифр.' };
  }
  return { ok: true, phone: trimmed };
}

export interface CheckoutPhoneNeed {
  /** Checkout chips: product-only orders ride the chosen method (DropoffPoint = carrier drop-off — delivery). */
  deliveryMethod: 'Pickup' | 'DropoffPoint' | 'Courier';
  /** True when any quote-print line delivers by courier (line snapshot). */
  hasCourierLines: boolean;
  /** Carrier ПВЗ picked on the checkout map (ozon/cdek/russianpost). */
  carrierPointChosen: boolean;
}

/**
 * The phone is required when the order travels to the customer through a
 * carrier or courier — someone must be reachable at the door/ПВЗ. Pure
 * provider self-pickup works without one (the customer shows up in person).
 */
export function checkoutNeedsPhone(need: CheckoutPhoneNeed): boolean {
  return (
    need.deliveryMethod !== 'Pickup' ||
    need.hasCourierLines ||
    need.carrierPointChosen
  );
}

export interface CheckoutPhoneGate {
  /** True when the field must be filled but is empty. */
  missing: boolean;
  /** ru error for an invalid typed value, or null. */
  error: string | null;
  /** Checkout is blocked until the phone is valid (or not required). */
  blocks: boolean;
}

/** One-shot gate for the checkout button. */
export function checkoutPhoneGate(raw: string, need: CheckoutPhoneNeed): CheckoutPhoneGate {
  const required = checkoutNeedsPhone(need);
  const parsed = parseCheckoutPhone(raw);
  if (!parsed.ok) {
    return { missing: false, error: parsed.error, blocks: true };
  }
  const missing = required && parsed.phone === null;
  return { missing, error: null, blocks: missing };
}
